import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HelicopterPackage } from "@/types/helicopter";
import { Clock, Check, ChevronRight, Crown } from "lucide-react";
import helicopterGallery1 from "@/assets/helicopter-gallery-1.jpg";
import helicopterGallery2 from "@/assets/helicopter-gallery-2.jpg";
import helicopterGallery3 from "@/assets/helicopter-gallery-3.jpg";
import helicopterGallery4 from "@/assets/helicopter-gallery-4.jpg";
import helicopterGrandExplorer from "@/assets/helicopter-grand-explorer.jpg";
import helicopterSkylineExpress from "@/assets/helicopter-skyline-express.jpg";
import helicopterIconicDubai from "@/assets/helicopter-iconic-dubai.jpg";

interface HelicopterPackageCardProps {
  package: HelicopterPackage;
  onBookNow?: (packageId: string) => void;
}

// Map package IDs to images for variety
const packageImages: Record<string, string> = {
  'skyline-express': helicopterSkylineExpress,
  'iconic-dubai': helicopterIconicDubai,
  'ultimate-city': helicopterGallery3,
  'grand-explorer': helicopterGrandExplorer,
};

const HelicopterPackageCard = ({ package: pkg, onBookNow }: HelicopterPackageCardProps) => {
  const displayLocations = pkg.locations.slice(0, 4);
  const hasMoreLocations = pkg.locations.length > 4;
  const packageImage = packageImages[pkg.id] || helicopterGallery1;

  return (
    <div className={`group relative flex flex-col bg-slate-900 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-gold/30 transition-all duration-500 hover:shadow-2xl hover:shadow-gold/10 ${pkg.isBestValue ? 'ring-2 ring-gold/50' : ''}`}>
      {/* Most Popular Badge */}
      {pkg.isBestValue && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
          <div className="bg-gradient-to-r from-gold/90 to-amber-500/90 text-gold-foreground px-6 py-2 text-sm font-bold uppercase tracking-wider rounded-b-xl flex items-center gap-2 shadow-lg">
            <Crown className="h-4 w-4" />
            Most Popular
          </div>
        </div>
      )}

      {/* Private Only Badge */}
      {pkg.isPrivateOnly && (
        <div className="absolute top-4 right-4 z-20">
          <div className="bg-slate-800/90 backdrop-blur-sm text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full border border-gold/30">
            Exclusive
          </div>
        </div>
      )}

      {/* Image Section */}
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img 
          src={packageImage} 
          alt={`${pkg.name} helicopter tour`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        
        {/* Duration Badge */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-slate-900/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-slate-600/50">
          <Clock className="h-4 w-4 text-gold" />
          <span className="text-sm font-medium text-white">{pkg.duration}</span>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col flex-1 p-5 sm:p-6">
        {/* Package Name */}
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
          {pkg.name}
        </h3>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-5">
          <span className="text-2xl sm:text-3xl font-bold text-gold">
            AED {pkg.price.toLocaleString()}
          </span>
          <span className="text-sm text-slate-400 lowercase">
            {pkg.priceLabel}
          </span>
        </div>

        {/* Locations/Features List */}
        <ul className="space-y-2.5 mb-6 flex-1">
          {displayLocations.map((location, index) => (
            <li 
              key={index} 
              className="text-sm text-slate-300 flex items-start gap-3"
            >
              <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" />
              <span className="line-clamp-1">{location}</span>
            </li>
          ))}
          {hasMoreLocations && (
            <Dialog>
              <DialogTrigger asChild>
                <li className="text-sm text-gold hover:text-gold/80 flex items-start gap-3 cursor-pointer transition-colors">
                  <Check className="h-4 w-4 mt-0.5 shrink-0 opacity-0" />
                  <span className="underline underline-offset-2">+{pkg.locations.length - 4} more locations</span>
                </li>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 max-h-[80vh] overflow-y-auto rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-white">{pkg.name}</DialogTitle>
                  <p className="text-sm text-slate-400">All sightseeing locations included</p>
                </DialogHeader>
                <ul className="space-y-3 mt-4">
                  {pkg.locations.map((location, index) => (
                    <li 
                      key={index} 
                      className="text-sm text-slate-200 flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors"
                    >
                      <span className="w-6 h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs font-medium shrink-0">
                        {index + 1}
                      </span>
                      <span>{location}</span>
                    </li>
                  ))}
                </ul>
              </DialogContent>
            </Dialog>
          )}
        </ul>

        {/* CTA Button */}
        <Button 
          className="w-full h-12 bg-slate-800 hover:bg-gold hover:text-gold-foreground text-white font-bold text-base rounded-xl border border-slate-600 hover:border-gold transition-all duration-300 group/btn"
          onClick={() => onBookNow?.(pkg.id)}
        >
          Book Now
          <ChevronRight className="h-5 w-5 ml-2 group-hover/btn:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );
};

export default HelicopterPackageCard;
